import { useEffect, useState } from 'react';

/**
 * Custom hook that returns a boolean for whether a media query matches.
 * Useful for responsive behavior and conditional rendering based on screen size.
 *
 * Common breakpoints:
 * - (max-width: 640px) - Mobile
 * - (max-width: 768px) - Tablet
 * - (min-width: 769px) - Desktop
 * - (max-width: 1024px) - Not large desktop
 *
 * @param query - The media query string (without 'only screen and')
 * @returns boolean - Whether the media query currently matches
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 640px)');
 * const isTablet = useMediaQuery('(max-width: 768px)');
 * const isDesktop = useMediaQuery('(min-width: 769px)');
 *
 * return isMobile ? <MobileMenu /> : <DesktopMenu />;
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    // Create media query list
    const mediaQueryList = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Create listener function
    const handleChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };

    // Add listener (modern API - addEventListener)
    // Fall back to addListener for older browsers
    if (mediaQueryList.addListener) {
      mediaQueryList.addListener(handleChange);
    } else {
      mediaQueryList.addEventListener('change', handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQueryList.removeListener) {
        mediaQueryList.removeListener(handleChange);
      } else {
        mediaQueryList.removeEventListener('change', handleChange);
      }
    };
  }, [query, isClient]);

  return matches;
}
